import React, { useCallback, useId, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './button.tsx';
import { Checkbox } from './checkbox.tsx';
import { Label } from './label.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './dialog.tsx';

/**
 * Promise-based in-app confirmation, a drop-in for `window.confirm`, which
 * embedded browsers (the desktop app's preview pane among them) answer with
 * `false` without showing anything.
 *
 *   const [confirm, confirmDialog] = useConfirmDialog();
 *   if (!(await confirm({ title: 'Publish v3?', description: '…', confirmLabel: 'Publish' }))) return;
 *   …
 *   return <>{…}{confirmDialog}</>;
 *
 * With `checkbox: { label }` the dialog shows one checkbox (unticked) and the
 * promise resolves `{ checked }` on confirm (still `false` on cancel);
 * `description` may then be a function of `checked`.
 */
export function useConfirmDialog() {
  const [state, setState] = useState(null);
  const [checked, setChecked] = useState(false);
  const resolver = useRef(null);
  const checkboxId = useId();

  const confirm = useCallback((options) => new Promise((resolve) => {
    const opts = typeof options === 'string' ? { description: options } : (options || {});
    resolver.current = resolve;
    setChecked(false);
    setState({
      checkbox: opts.checkbox && opts.checkbox.label ? { label: opts.checkbox.label } : null,
      title: opts.title || 'Are you sure?',
      description: opts.description || '',
      confirmLabel: opts.confirmLabel || 'Confirm',
      cancelLabel: opts.cancelLabel || 'Cancel',
      destructive: opts.destructive === true,
    });
  }), []);

  const settle = useCallback((value) => {
    const resolve = resolver.current;
    const withCheckbox = Boolean(state && state.checkbox);
    resolver.current = null;
    setState(null);
    if (resolve) resolve(value && withCheckbox ? { checked } : value);
  }, [state, checked]);

  const description = state && typeof state.description === 'function' ? state.description(checked) : state && state.description;

  // Stacked: a confirm asked from inside a stacked dialog (the envelope modal)
  // would otherwise open underneath it, invisible but still swallowing clicks.
  const dialog = (
    <Dialog open={state !== null} onOpenChange={(open) => { if (!open) settle(false); }}>
      {state ? (
        <DialogContent size="confirm" stacked className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6">
              <AlertTriangle className={`h-5 w-5 shrink-0 ${state.destructive ? 'text-destructive' : 'text-amber-600 dark:text-amber-400'}`} aria-hidden />
              {state.title}
            </DialogTitle>
            {description ? (
              <DialogDescription className="whitespace-pre-line pt-1 text-left">{description}</DialogDescription>
            ) : null}
          </DialogHeader>
          {state.checkbox ? (
            <div className="flex items-center gap-2">
              <Checkbox id={checkboxId} checked={checked} onCheckedChange={(v) => setChecked(v === true)} />
              <Label htmlFor={checkboxId} className="text-sm font-normal cursor-pointer">{state.checkbox.label}</Label>
            </div>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => settle(false)}>{state.cancelLabel}</Button>
            <Button type="button" variant={state.destructive ? 'destructive' : 'default'} onClick={() => settle(true)} autoFocus>{state.confirmLabel}</Button>
          </DialogFooter>
        </DialogContent>
      ) : null}
    </Dialog>
  );

  return [confirm, dialog];
}

export default useConfirmDialog;
